import { Loader2 } from "lucide-react";
import React from "react";

interface LoadingSpinnerProps {
  size?: "sm" | "md" | "lg";
  color?: "blue" | "purple" | "white" | "gray";
  text?: string;
  fullScreen?: boolean;
}

const LoadingSpinner: React.FC<LoadingSpinnerProps> = ({
  size = "md",
  color = "blue",
  text,
  fullScreen = false,
}) => {
  // Size mappings - enhanced for Culture Lab
  const sizeClasses = {
    sm: "w-4 h-4",
    md: "w-6 h-6",
    lg: "w-8 h-8",
  };

  const textSizeClasses = {
    sm: "text-xs",
    md: "text-sm",
    lg: "text-base",
  };

  // Enhanced color mappings for dark theme
  const colorClasses = {
    blue: "text-blue-400",
    purple: "text-purple-400",
    white: "text-white",
    gray: "text-gray-400",
  };

  const textColorClasses = {
    blue: "text-blue-300",
    purple: "text-purple-300",
    white: "text-white",
    gray: "text-gray-300",
  };

  // Enhanced fullScreen spinner ring colors
  const ringColorClasses = {
    blue: "border-blue-500/30 border-t-blue-500",
    purple: "border-purple-500/30 border-t-purple-500",
    white: "border-white/30 border-t-white",
    gray: "border-gray-500/30 border-t-gray-500",
  };

  // Simple spinner for inline use
  const spinner = (
    <Loader2
      className={`${sizeClasses[size]} ${colorClasses[color]} animate-spin`}
    />
  );

  const content = (
    <div className="flex flex-col items-center justify-center space-y-3">
      {spinner}
      {text && (
        <p
          className={`${textSizeClasses[size]} font-medium ${textColorClasses[color]}`}
        >
          {text}
        </p>
      )}
    </div>
  );

  if (fullScreen) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/80 backdrop-blur-sm">
        <div className="bg-slate-800/60 backdrop-blur-sm rounded-2xl p-8 shadow-2xl max-w-sm mx-4 border border-slate-700/50">
          <div className="flex flex-col items-center justify-center space-y-4">
            <div className="relative">
              {/* Enhanced outer rotating ring - Culture Lab style */}
              <div
                className={`w-16 h-16 border-4 ${ringColorClasses[color]} rounded-full animate-spin`}
              >
                <div className="w-full h-full border-4 border-transparent rounded-full"></div>
              </div>

              {/* Inner pulsing ring for extra sophistication */}
              <div
                className={`absolute inset-2 border-2 ${ringColorClasses[color]} rounded-full animate-pulse opacity-50`}
              ></div>

              {/* Center icon */}
              <div className="absolute inset-0 flex items-center justify-center">
                <Loader2
                  className={`w-6 h-6 ${colorClasses[color]} animate-spin`}
                />
              </div>
            </div>

            {text && (
              <p
                className={`text-base font-medium ${textColorClasses[color]} text-center`}
              >
                {text}
              </p>
            )}

            {/* Subtle loading indicator dots */}
            <div className="flex space-x-1">
              <div
                className={`w-2 h-2 ${colorClasses[color]} rounded-full animate-bounce`}
              ></div>
              <div
                className={`w-2 h-2 ${colorClasses[color]} rounded-full animate-bounce delay-100`}
              ></div>
              <div
                className={`w-2 h-2 ${colorClasses[color]} rounded-full animate-bounce delay-200`}
              ></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return content;
};

export default LoadingSpinner;
